import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bot,
  RefreshCw,
  Play,
  Pause,
  ExternalLink,
  GitBranch,
  Shield,
  Zap,
  Clock,
  AlertTriangle,
  Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { workflowApi } from '@/services/api/workflowApi';
import * as errorUtils from '@/utils/errors';

interface AgentWorkflow {
  _id: string;
  id?: string;
  name: string;
  description?: string;
  status: 'draft' | 'published';
  type: string;
  agentConfig?: {
    autonomyLevel?: 'supervised' | 'guided' | 'autonomous';
    generation?: number;
    maxGenerations?: number;
    capabilities?: string[];
    originalIntent?: string;
  };
  executionCount?: number;
  lastExecutedAt?: string;
  updatedAt?: string;
  createdAt?: string;
}

const autonomyColors: Record<string, string> = {
  supervised: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  guided: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  autonomous: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
};

function formatTimeAgo(dateStr?: string): string {
  if (!dateStr) return 'Never';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export const WorkflowAgentsTab: React.FC = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<AgentWorkflow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await workflowApi.listWorkflows({ type: 'agent' });
      const list = result?.data?.workflows || result?.workflows || result?.data || [];
      setAgents(Array.isArray(list) ? list : []);
    } catch (err: unknown) {
      setError(errorUtils.getErrorMessageOrFallback(err, 'Failed to load workflow agents'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const handleOpenEditor = (workflowId: string) => {
    navigate(`/workflow-editor/${workflowId}`);
  };

  const handleToggleStatus = async (agent: AgentWorkflow) => {
    const id = agent.id || agent._id;
    const newStatus = agent.status === 'published' ? 'draft' : 'published';
    setTogglingId(id);
    try {
      await workflowApi.patch(`/api/workflows/${id}`, { status: newStatus });
      setAgents(prev =>
        prev.map(a => (a.id || a._id) === id ? { ...a, status: newStatus } : a)
      );
    } catch {
      // Refresh list on error
      fetchAgents();
    } finally {
      setTogglingId(null);
    }
  };

  const handleCreateAgent = () => {
    navigate('/workflow-configuration');
  };

  if (loading && agents.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!loading && agents.length === 0 && !error) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-muted-foreground p-8">
        <Bot className="w-16 h-16 mb-4 opacity-40" />
        <h3 className="text-lg font-medium text-foreground mb-1">No Workflow Agents</h3>
        <p className="text-sm text-center mb-6 max-w-md">
          Create an agent-type workflow to enable autonomous monitoring, LLM reasoning,
          and supervised self-modification.
        </p>
        <Button onClick={handleCreateAgent}>
          <Plus className="w-4 h-4 mr-2" />
          Create Agent Workflow
        </Button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header bar */}
      <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-primary" />
          <span className="font-medium">
            {agents.length} agent{agents.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={fetchAgents} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={handleCreateAgent}>
            <Plus className="w-4 h-4 mr-1" />
            New Agent
          </Button>
        </div>
      </div>

      {error && (
        <div className="mx-4 mt-3 flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Agent cards */}
      <ScrollArea className="flex-1">
        <div className="p-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {agents.map(agent => {
            const id = agent.id || agent._id;
            const autonomy = agent.agentConfig?.autonomyLevel || 'guided';
            const gen = agent.agentConfig?.generation ?? 0;
            const maxGen = agent.agentConfig?.maxGenerations;
            const isActive = agent.status === 'published';

            return (
              <div
                key={id}
                className="border rounded-lg bg-card hover:border-primary/40 transition-colors"
              >
                {/* Card header */}
                <div className="p-4 pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h4 className="font-medium text-sm truncate">{agent.name}</h4>
                      {agent.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                          {agent.description}
                        </p>
                      )}
                    </div>
                    <Badge
                      variant="outline"
                      className={isActive
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-300'
                        : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                      }
                    >
                      {isActive ? 'Active' : 'Paused'}
                    </Badge>
                  </div>
                </div>

                {/* Stats row */}
                <div className="px-4 pb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Shield className="w-3 h-3" />
                    <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 ${autonomyColors[autonomy] || ''}`}>
                      {autonomy}
                    </Badge>
                  </span>
                  <span className="flex items-center gap-1 font-mono">
                    <GitBranch className="w-3 h-3" />
                    Gen {gen}{maxGen ? `/${maxGen}` : ''}
                  </span>
                  <span className="flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    {agent.executionCount ?? 0} runs
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatTimeAgo(agent.lastExecutedAt)}
                  </span>
                </div>

                {/* Capabilities */}
                {agent.agentConfig?.capabilities && agent.agentConfig.capabilities.length > 0 && (
                  <div className="px-4 pb-3 flex flex-wrap gap-1">
                    {agent.agentConfig.capabilities.slice(0, 4).map(cap => (
                      <Badge key={cap} variant="outline" className="text-[10px] px-1.5 py-0">
                        {cap}
                      </Badge>
                    ))}
                    {agent.agentConfig.capabilities.length > 4 && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        +{agent.agentConfig.capabilities.length - 4}
                      </Badge>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="px-4 py-3 border-t flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleOpenEditor(id)}
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                    Open Editor
                  </Button>
                  <Button
                    variant={isActive ? 'outline' : 'default'}
                    size="sm"
                    onClick={() => handleToggleStatus(agent)}
                    disabled={togglingId === id}
                  >
                    {isActive
                      ? <><Pause className="w-3.5 h-3.5 mr-1" /> Pause</>
                      : <><Play className="w-3.5 h-3.5 mr-1" /> Resume</>
                    }
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};
